import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import api from '../utils/api';
import {
  Truck, UploadSimple, DownloadSimple, PaperPlaneTilt, Trash, X, PencilSimple,
  Clock, MapPin, User, Package, CaretDown, CaretUp, ArrowsDownUp, Warning, CheckCircle, WifiHigh, WifiSlash, DotsThree,
  Camera, Eye, MapPinLine, ListChecks
} from '@phosphor-icons/react';
import toast, { Toaster } from 'react-hot-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { AuthImage } from '../components/AuthImage';
import { validateRut, formatRut, getRutErrorMessage } from '../utils/rutValidator';

const ESTADOS = {
  pendiente: { label: 'PENDIENTE', color: 'bg-neutral-100 text-neutral-800 border border-neutral-300', dot: 'bg-neutral-500', pulse: false },
  planificado: { label: 'PLANIFICADO', color: 'bg-cyan-50 text-cyan-700 border border-cyan-200', dot: 'bg-cyan-500', pulse: true },
  notificado: { label: 'NOTIFICADO', color: 'bg-blue-50 text-blue-700 border border-blue-200', dot: 'bg-blue-500', pulse: true },
  en_anden: { label: 'EN ANDÉN', color: 'bg-amber-50 text-amber-800 border border-amber-300', dot: 'bg-amber-500', pulse: true },
  cargando: { label: 'CARGADO', color: 'bg-orange-50 text-orange-800 border border-orange-300', dot: 'bg-orange-500', pulse: true },
  en_transito: { label: 'EN TRÁNSITO', color: 'bg-purple-50 text-purple-700 border border-purple-200', dot: 'bg-purple-500', pulse: true },
  entregado: { label: 'ENTREGADO', color: 'bg-emerald-50 text-emerald-700 border border-emerald-200', dot: 'bg-emerald-500', pulse: false },
  completado: { label: 'COMPLETADO', color: 'bg-emerald-100 text-emerald-800 border border-emerald-300', dot: 'bg-emerald-600', pulse: false },
  cancelado: { label: 'CANCELADO', color: 'bg-red-50 text-red-700 border border-red-200', dot: 'bg-red-500', pulse: false },
  borrado: { label: 'BORRADO', color: 'bg-neutral-200 text-neutral-500 border border-neutral-400 line-through', dot: 'bg-neutral-400', pulse: false },
};

export default function Shipments() {
  const { user, token } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [filterEstado, setFilterEstado] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [callForm, setCallForm] = useState({ anden: '', hora: '', mensaje_adicional: '' });
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [flashRow, setFlashRow] = useState(null);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [evidenceShipment, setEvidenceShipment] = useState(null);
  const [evidences, setEvidences] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loadingEvidences, setLoadingEvidences] = useState(false);
  const [evidenceCounts, setEvidenceCounts] = useState({});
  const [lightboxFileId, setLightboxFileId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editShipment, setEditShipment] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [editRutError, setEditRutError] = useState('');
  const [sortBy, setSortBy] = useState(null); // 'numero_transporte' | 'conductor_nombre' | 'vehiculo_patente' | 'destino'
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  const todayISO = new Date().toISOString().slice(0, 10);
  const weekAgoISO = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [fechaDesde, setFechaDesde] = useState(weekAgoISO);
  const [fechaHasta, setFechaHasta] = useState(todayISO);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const fileInputRef = useRef(null);

  const toggleSelectOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const visibleIds = sortedShipments.map(s => s.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        visibleIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds(prev => new Set([...prev, ...visibleIds]));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`¿Borrar ${ids.length} viaje(s) seleccionado(s)? Podrás recuperarlos filtrando por estado "borrado".`)) return;
    setBulkDeleting(true);
    try {
      const res = await api.post('/shipments/bulk-delete', { ids });
      const deleted = res.data?.deleted ?? ids.length;
      setShipments(prev => prev.filter(s => !ids.includes(s.id)));
      toast.success(`${deleted} viaje(s) borrado(s)`);
      clearSelection();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error borrando viajes');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const sortedShipments = React.useMemo(() => {
    if (!sortBy) return shipments;
    const copy = [...shipments];
    copy.sort((a, b) => {
      const va = (a[sortBy] ?? '').toString().toLowerCase();
      const vb = (b[sortBy] ?? '').toString().toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [shipments, sortBy, sortDir]);

  // WebSocket URL - convertir http(s) a ws(s)
  const wsUrl = token
    ? `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, 'ws')}/api/ws/shipments?token=${token}`
    : null;

  const handleWsMessage = (msg) => {
    if (msg.event === 'connected') {
      setWsConnected(true);
      return;
    }
    // Actualizar shipment en tiempo real
    if (msg.event === 'shipment_updated' || msg.event === 'shipment_called') {
      const updatedShipment = msg.data.shipment;
      if (!updatedShipment) return;
      
      setShipments(prev => {
        const idx = prev.findIndex(s => s.id === updatedShipment.id);
        if (idx === -1) {
          // Nuevo shipment, agregarlo al array solo si no existe ya
          const exists = prev.some(s => s.id === updatedShipment.id);
          if (!exists) {
            return [...prev, updatedShipment];
          }
          return prev;
        }
        // Actualizar shipment existente
        const copy = [...prev];
        copy[idx] = { ...copy[idx], ...updatedShipment };
        return copy;
      });
      
      // Flash effect
      setFlashRow(updatedShipment.id);
      setTimeout(() => setFlashRow(null), 1500);
    }
    if (msg.event === 'evidence_uploaded') {
      toast.success(`📸 Nueva evidencia en viaje`, { duration: 3000 });
    }
    if (msg.event === 'shipment_deleted') {
      const deletedId = msg.data?.shipment_id;
      if (deletedId) {
        setShipments(prev => prev.filter(s => s.id !== deletedId));
      }
    }
  };

  useWebSocket(wsUrl, handleWsMessage, !!token);

  useEffect(() => {
    fetchShipments();
    // eslint-disable-next-line
  }, [filterEstado, fechaDesde, fechaHasta]);

  // Fallback polling si WS no está conectado
  useEffect(() => {
    if (wsConnected) return;
    const interval = setInterval(fetchShipments, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [wsConnected, filterEstado, fechaDesde, fechaHasta]);

  const fetchShipments = async () => {
    try {
      const params = new URLSearchParams();
      if (filterEstado) params.append('estado', filterEstado);
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await api.get(`/shipments${qs}`);
      
      // Eliminar duplicados basados en ID antes de actualizar el estado
      const uniqueShipments = res.data.reduce((acc, current) => {
        const exists = acc.find(item => item.id === current.id);
        if (!exists) {
          acc.push(current);
        }
        return acc;
      }, []);
      
      setShipments(uniqueShipments);
      
      // Fetch counts de evidencias en paralelo
      const counts = {};
      await Promise.all(uniqueShipments.map(async (s) => {
        try {
          const evRes = await api.get(`/shipments/${s.id}/evidences`);
          counts[s.id] = evRes.data.length;
        } catch (e) {
          counts[s.id] = 0;
        }
      }));
      setEvidenceCounts(counts);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openEvidenceModal = async (shipment) => {
    setEvidenceShipment(shipment);
    setShowEvidenceModal(true);
    setLoadingEvidences(true);
    try {
      const [evRes, delRes] = await Promise.all([
        api.get(`/shipments/${shipment.id}/evidences`),
        api.get(`/shipments/${shipment.id}/deliveries`)
      ]);
      setEvidences(evRes.data);
      setDeliveries(delRes.data);
    } catch (error) {
      toast.error('Error cargando evidencias');
    } finally {
      setLoadingEvidences(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/shipments/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sroute_template_viajes.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template descargado');
    } catch (error) {
      toast.error('Error descargando template');
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Selecciona un archivo Excel');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    try {
      const res = await api.post('/shipments/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${res.data.creados} viajes cargados exitosamente`);
      if (res.data.errores?.length > 0) {
        toast.error(`${res.data.errores.length} filas con errores`);
      }
      setShowUploadModal(false);
      setUploadFile(null);
      fetchShipments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error subiendo archivo');
    } finally {
      setUploading(false);
    }
  };

  const openCallModal = (shipment) => {
    setSelectedShipment(shipment);
    setCallForm({
      anden: shipment.anden || '',
      hora: shipment.hora_carga || '',
      mensaje_adicional: ''
    });
    setShowCallModal(true);
  };

  const handleCallToLoad = async () => {
    if (!callForm.anden || !callForm.hora) {
      toast.error('Andén y hora son requeridos');
      return;
    }
    setSending(true);
    try {
      // Si el estado es "pendiente", primero cambiar a "planificado"
      if (selectedShipment.estado === 'pendiente') {
        await api.patch(`/shipments/${selectedShipment.id}/status`, { estado: 'planificado' });
        toast.success('Estado cambiado a PLANIFICADO');
      }
      
      // Luego enviar el mensaje de WhatsApp
      await api.post(`/shipments/${selectedShipment.id}/call-to-load`, callForm);
      toast.success('WhatsApp enviado al conductor');
      setShowCallModal(false);
      fetchShipments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error enviando WhatsApp');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (id, estado) => {
    try {
      await api.patch(`/shipments/${id}/status`, { estado });
      if (estado === 'planificado') {
        toast.success('WhatsApp enviado con link de guía', { duration: 4000 });
      } else {
        toast.success('Estado actualizado');
      }
      fetchShipments();
    } catch (error) {
      toast.error('Error actualizando estado');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Borrar este viaje? Podrás recuperarlo filtrando por estado "borrado".')) return;
    try {
      await api.delete(`/shipments/${id}`);
      // Optimistic: remove from list immediately (WS also broadcasts)
      setShipments(prev => prev.filter(s => s.id !== id));
      toast.success('Viaje borrado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error borrando');
    }
  };

  const openEditModal = (shipment) => {
    setEditShipment(shipment);
    setEditRutError('');
    setEditForm({
      numero_transporte: shipment.numero_transporte || '',
      conductor_nombre: shipment.conductor_nombre || '',
      conductor_telefono: shipment.conductor_telefono || '',
      conductor_rut: shipment.conductor_rut || '',
      vehiculo_patente: shipment.vehiculo_patente || '',
      origen: shipment.origen || '',
      destino: shipment.destino || '',
      fecha_carga: shipment.fecha_carga || '',
      hora_carga: shipment.hora_carga || '',
      anden: shipment.anden || '',
      observaciones: shipment.observaciones || '',
    });
    setShowEditModal(true);
  };

  const searchDriverByRut = async (rut) => {
    if (!rut || rut.trim() === '') return;
    
    // Solo buscar si el RUT es válido
    if (!validateRut(rut)) return;
    
    try {
      const res = await api.get('/drivers');
      const driver = res.data.find(d => d.rut === rut && d.activo);
      if (driver) {
        setEditForm(prev => ({ 
          ...prev, 
          conductor_nombre: driver.nombre,
          conductor_telefono: driver.telefono 
        }));
        toast.success('Conductor encontrado en maestro');
      }
    } catch (err) {
      console.error('Error buscando conductor', err);
    }
  };

  const handleEditRutChange = async (e) => {
    const value = e.target.value;
    const formatted = formatRut(value);
    setEditForm(prev => ({ ...prev, conductor_rut: formatted }));
    
    // Validar RUT
    const errorMsg = getRutErrorMessage(formatted);
    setEditRutError(errorMsg);
  };

  const handleEditRutBlur = async () => {
    // Buscar conductor cuando se sale del campo (TAB o click fuera)
    await searchDriverByRut(editForm.conductor_rut);
  };

  const handleSaveEdit = async () => {
    if (!editShipment) return;
    // Validaciones mínimas
    if (!editForm.numero_transporte || !editForm.conductor_nombre || !editForm.conductor_telefono) {
      toast.error('Transporte, conductor y teléfono son requeridos');
      return;
    }
    
    // Validar RUT si está presente
    if (editForm.conductor_rut && !validateRut(editForm.conductor_rut)) {
      setEditRutError('RUT inválido');
      toast.error('Por favor, ingresa un RUT válido');
      return;
    }
    
    setSavingEdit(true);
    try {
      const res = await api.patch(`/shipments/${editShipment.id}`, editForm);
      // Optimistic UI update - WS also broadcasts
      setShipments(prev => prev.map(s => s.id === editShipment.id ? { ...s, ...res.data } : s));
      toast.success('Viaje actualizado');
      setShowEditModal(false);
      setEditShipment(null);
      setEditRutError('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error actualizando viaje');
    } finally {
      setSavingEdit(false);
    }
  };

  const currentTime = new Date().toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });

  return (
    <div data-testid="shipments-page">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-end gap-6 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-2">
              Wallboard · Departures Board
            </p>
            <h1 className="text-4xl font-bold text-neutral-900 tracking-tight">
              Viajes
            </h1>
          </div>
          {/* Date range */}
          <div className="flex items-end gap-3 pb-1 translate-y-5" data-testid="date-range-filter">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-1 font-semibold">
                Desde
              </label>
              <input
                type="date"
                value={fechaDesde}
                max={fechaHasta || undefined}
                onChange={(e) => setFechaDesde(e.target.value)}
                data-testid="fecha-desde-input"
                className="px-3 py-2 border border-neutral-300 rounded-md text-sm font-mono text-neutral-800 focus:outline-none focus:ring-2 focus:ring-blue-700"
              />
            </div>
            <span className="pb-2 text-neutral-400 text-sm">—</span>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-1 font-semibold">
                Hasta
              </label>
              <input
                type="date"
                value={fechaHasta}
                min={fechaDesde || undefined}
                onChange={(e) => setFechaHasta(e.target.value)}
                data-testid="fecha-hasta-input"
                className="px-3 py-2 border border-neutral-300 rounded-md text-sm font-mono text-neutral-800 focus:outline-none focus:ring-2 focus:ring-blue-700"
              />
            </div>
            <button
              onClick={() => { setFechaDesde(weekAgoISO); setFechaHasta(todayISO); }}
              data-testid="fecha-reset-btn"
              title="Restablecer a los últimos 7 días"
              className="pb-2 text-[10px] uppercase tracking-[0.25em] text-blue-700 hover:text-blue-900 font-semibold"
            >
              Últimos 7 días
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownloadTemplate}
            data-testid="download-template-btn"
            className="flex items-center px-4 py-2.5 border border-neutral-300 text-neutral-700 rounded-md hover:bg-neutral-50 text-sm font-medium"
          >
            <DownloadSimple size={16} className="mr-2" />
            Template Excel
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            data-testid="upload-shipments-btn"
            className="flex items-center px-4 py-2.5 bg-blue-700 text-white rounded-md hover:bg-blue-800 text-sm font-medium"
          >
            <UploadSimple size={16} className="mr-2" weight="bold" />
            Cargar Excel
          </button>
        </div>
      </div>

      {/* Filters - Tabs unificadas con indicador inferior */}
      <div className="mb-4 border-b border-neutral-200 overflow-x-auto" data-testid="shipments-filter-tabs">
        <div className="flex items-stretch min-w-max">
          <button
            onClick={() => setFilterEstado('')}
            data-testid="filter-all-btn"
            className={`relative px-4 py-2.5 -mb-px text-xs uppercase tracking-widest font-semibold border-b-2 transition-colors focus:outline-none ${
              filterEstado === ''
                ? 'border-blue-700 text-neutral-900'
                : 'border-transparent text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50'
            }`}
          >
            Todos
            <span className={`ml-2 inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 text-[10px] rounded font-mono ${
              filterEstado === '' ? 'bg-blue-700 text-white' : 'bg-neutral-200 text-neutral-600'
            }`}>
              {shipments.length}
            </span>
          </button>
          {Object.keys(ESTADOS).map(key => {
            const isActive = filterEstado === key;
            const est = ESTADOS[key];
            return (
              <button
                key={key}
                onClick={() => setFilterEstado(key)}
                data-testid={`filter-${key}-btn`}
                className={`relative px-4 py-2.5 -mb-px text-xs uppercase tracking-widest font-semibold border-b-2 transition-colors focus:outline-none flex items-center gap-2 ${
                  isActive
                    ? 'border-blue-700 text-neutral-900'
                    : 'border-transparent text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${est.dot} ${isActive && est.pulse ? 'animate-pulse' : ''}`}></span>
                {est.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Board - Swiss white style */}
      <div className="bg-white rounded-md border border-neutral-200 overflow-visible" data-testid="departures-board">
        {/* Board Header */}
        <div className="bg-neutral-50 px-6 py-3 border-b border-neutral-200 flex items-center justify-between rounded-t-md">
          <div className="flex items-center">
            <Truck size={18} className="text-blue-700 mr-2" weight="fill" />
            <span className="text-neutral-800 font-mono text-xs uppercase tracking-[0.3em] font-bold">
              Departures · Live
            </span>
          </div>
          <div className="flex items-center gap-4 font-mono text-xs text-neutral-500">
            <span className="flex items-center" data-testid="ws-status">
              {wsConnected ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2 animate-pulse"></span>
                  <WifiHigh size={14} className="mr-1 text-emerald-600" />
                  <span className="text-emerald-700 font-semibold">LIVE</span>
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-amber-500 mr-2"></span>
                  <WifiSlash size={14} className="mr-1 text-amber-600" />
                  <span className="text-amber-700 font-semibold">POLLING</span>
                </>
              )}
            </span>
            <span className="text-neutral-700 font-semibold">{currentTime}</span>
          </div>
        </div>

        {/* Column Headers - clickable sortable */}
        <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-neutral-50/50 border-b border-neutral-200 text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-semibold">
          {[
            { key: 'numero_transporte', label: 'Transporte', span: 'col-span-2', withCheckbox: true },
            { key: 'conductor_nombre', label: 'Conductor', span: 'col-span-2' },
            { key: 'vehiculo_patente', label: 'Vehículo', span: 'col-span-1' },
            { key: 'destino', label: 'Destino', span: 'col-span-2' },
          ].map(col => {
            const active = sortBy === col.key;
            const allVisibleSelected = sortedShipments.length > 0 && sortedShipments.every(s => selectedIds.has(s.id));
            const someVisibleSelected = sortedShipments.some(s => selectedIds.has(s.id));
            return (
              <div key={col.key} className={`${col.span} flex items-center gap-2`}>
                {col.withCheckbox && (
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    ref={el => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                    onChange={toggleSelectAll}
                    data-testid="select-all-checkbox"
                    title={allVisibleSelected ? 'Deseleccionar todos' : 'Seleccionar todos los visibles'}
                    className="w-4 h-4 rounded border-neutral-300 text-blue-700 focus:ring-2 focus:ring-blue-700 cursor-pointer"
                  />
                )}
                <button
                  onClick={() => handleSort(col.key)}
                  data-testid={`sort-${col.key}-btn`}
                  title={`Ordenar por ${col.label}`}
                  className={`flex items-center gap-1 text-left uppercase tracking-[0.25em] font-semibold hover:text-neutral-900 transition-colors focus:outline-none ${
                    active ? 'text-blue-700' : 'text-neutral-500'
                  }`}
                >
                  <span>{col.label}</span>
                  {active ? (
                    sortDir === 'asc'
                      ? <CaretUp size={10} weight="bold" data-testid={`sort-${col.key}-asc`} />
                      : <CaretDown size={10} weight="bold" data-testid={`sort-${col.key}-desc`} />
                  ) : (
                    <ArrowsDownUp size={10} weight="bold" className="opacity-30" />
                  )}
                </button>
              </div>
            );
          })}
          <div className="col-span-1 text-center">Hora</div>
          <div className="col-span-1 text-center">Andén</div>
          <div className="col-span-2 text-center">Estado</div>
          <div className="col-span-1 text-right">Acción</div>
        </div>

        {/* Rows - NO internal scroll, page scrolls naturally */}
        <div className="divide-y divide-neutral-100">
          {loading ? (
            <div className="p-12 text-center text-neutral-500 text-sm">Cargando...</div>
          ) : shipments.length === 0 ? (
            <div className="p-16 text-center">
              <Package size={48} className="mx-auto text-neutral-300 mb-3" />
              <p className="text-neutral-500 text-sm uppercase tracking-widest">
                No hay viajes
              </p>
              <p className="text-neutral-400 text-xs mt-2">Carga un Excel para comenzar</p>
            </div>
          ) : (
            sortedShipments.map(s => {
              const est = ESTADOS[s.estado] || ESTADOS.pendiente;
              return (
                <div className={`grid grid-cols-12 gap-4 px-6 py-4 hover:bg-neutral-50 items-center ${
                    flashRow === s.id ? 'bg-amber-100' : ''
                  } ${selectedIds.has(s.id) ? 'bg-blue-50/60' : ''}`}
                  style={{ transition: 'background-color 0.4s ease' }}
                  data-testid={`shipment-row-${s.id}`}
                  key={s.id}
                >
                  <div className="col-span-2 flex items-center gap-2 font-mono">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(s.id)}
                      onChange={() => toggleSelectOne(s.id)}
                      data-testid={`row-checkbox-${s.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 rounded border-neutral-300 text-blue-700 focus:ring-2 focus:ring-blue-700 cursor-pointer"
                    />
                    <div className="min-w-0">
                      <p className="text-blue-700 font-bold text-sm tracking-wider truncate">{s.numero_transporte}</p>
                      <p className="text-neutral-400 text-[10px] uppercase tracking-widest mt-0.5">{s.fecha_carga}</p>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <p className="font-medium text-sm text-neutral-900">{s.conductor_nombre}</p>
                    <p className="text-neutral-500 text-xs font-mono">{s.conductor_telefono}</p>
                  </div>
                  <div className="col-span-1 font-mono text-neutral-900 text-sm font-medium">
                    {s.vehiculo_patente}
                  </div>
                  <div className="col-span-2 text-sm">
                    <p className="truncate text-neutral-900 font-medium">{s.destino}</p>
                    <p className="text-neutral-500 text-xs truncate">← {s.origen}</p>
                  </div>
                  <div className="col-span-1 text-center">
                    <p className="text-neutral-900 font-mono text-lg font-bold tracking-wider">{s.hora_carga}</p>
                  </div>
                  <div className="col-span-1 text-center">
                    {s.anden ? (
                      <span className="inline-block px-2 py-1 bg-amber-100 text-amber-900 border border-amber-300 font-mono font-bold text-base rounded">
                        {s.anden}
                      </span>
                    ) : (
                      <span className="text-neutral-300 font-mono text-sm">—</span>
                    )}
                  </div>
                  <div className="col-span-2 text-center">
                    <div className={`inline-flex items-center px-3 py-1.5 rounded text-[10px] uppercase tracking-widest font-bold ${est.color}`}>
                      <span className={`w-1.5 h-1.5 rounded-full mr-2 ${est.dot} ${est.pulse ? 'animate-pulse' : ''}`}></span>
                      {est.label}
                    </div>
                  </div>
                  <div className="col-span-1 flex items-center justify-end gap-1">
                    {evidenceCounts[s.id] > 0 && (
                      <button
                        onClick={() => openEvidenceModal(s)}
                        data-testid={`view-evidence-btn-${s.id}`}
                        title={`Ver ${evidenceCounts[s.id]} evidencia(s)`}
                        className="p-2 bg-purple-100 hover:bg-purple-200 text-purple-800 rounded transition-colors relative"
                      >
                        <Camera size={14} weight="bold" />
                        <span className="absolute -top-1 -right-1 bg-purple-600 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                          {evidenceCounts[s.id]}
                        </span>
                      </button>
                    )}
                    <button
                      onClick={() => openCallModal(s)}
                      data-testid={`call-load-btn-${s.id}`}
                      title="Llamar a carga"
                      className="p-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                    >
                      <PaperPlaneTilt size={14} weight="fill" />
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid={`status-menu-${s.id}`}
                          className="p-2 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-700"
                          title="Cambiar estado"
                        >
                          <DotsThree size={16} weight="bold" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56 z-50">
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-neutral-500">
                          Cambiar estado
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {Object.keys(ESTADOS).map(k => (
                          <DropdownMenuItem
                            key={k}
                            data-testid={`status-option-${k}-${s.id}`}
                            onSelect={() => handleStatusChange(s.id, k)}
                            className="cursor-pointer"
                          >
                            <span className={`w-2 h-2 rounded-full mr-2 ${ESTADOS[k].dot}`}></span>
                            <span className="text-xs uppercase tracking-widest">{ESTADOS[k].label}</span>
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          data-testid={`edit-shipment-${s.id}`}
                          onSelect={() => openEditModal(s)}
                          className="cursor-pointer"
                        >
                          <PencilSimple size={14} className="mr-2" />
                          <span className="text-xs uppercase tracking-widest">Editar</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-testid={`delete-shipment-${s.id}`}
                          onSelect={() => handleDelete(s.id)}
                          className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                        >
                          <Trash size={14} className="mr-2" />
                          <span className="text-xs uppercase tracking-widest">Borrar</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="upload-modal">
          <div className="bg-white rounded-md max-w-lg w-full">
            <div className="flex items-center justify-between p-6 border-b border-neutral-200">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-1">Import</p>
                <h2 className="text-xl font-bold text-neutral-900">Cargar Viajes desde Excel</h2>
              </div>
              <button onClick={() => { setShowUploadModal(false); setUploadFile(null); }} className="p-1 hover:bg-neutral-100 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-md text-sm text-neutral-800">
                <p className="font-medium mb-1">¿Primera vez?</p>
                <p className="text-xs">Descarga el template para asegurar el formato correcto de columnas.</p>
                <button
                  onClick={handleDownloadTemplate}
                  data-testid="download-template-modal-btn"
                  className="mt-2 flex items-center text-blue-700 hover:underline text-xs font-medium"
                >
                  <DownloadSimple size={14} className="mr-1" />
                  Descargar template
                </button>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">
                  Archivo Excel (.xlsx)
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  data-testid="upload-file-input"
                  className="w-full px-4 py-3 border border-dashed border-neutral-300 rounded-md text-sm hover:border-blue-700 cursor-pointer"
                />
                {uploadFile && (
                  <p className="mt-2 text-xs text-neutral-600 flex items-center">
                    <CheckCircle size={14} weight="fill" className="text-emerald-600 mr-1" />
                    {uploadFile.name}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-neutral-200">
                <button
                  onClick={() => { setShowUploadModal(false); setUploadFile(null); }}
                  className="px-4 py-2 bg-neutral-100 text-neutral-700 rounded-md hover:bg-neutral-200 text-sm font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploading || !uploadFile}
                  data-testid="submit-upload-btn"
                  className="px-4 py-2 bg-blue-700 text-white rounded-md hover:bg-blue-800 disabled:opacity-60 text-sm font-medium"
                >
                  {uploading ? 'Cargando...' : 'Cargar Viajes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Call to Load Modal */}
      {showCallModal && selectedShipment && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="call-modal">
          <div className="bg-white rounded-md max-w-lg w-full">
            <div className="p-6 border-b border-neutral-200 bg-neutral-950 text-white">
              <p className="text-xs uppercase tracking-[0.3em] text-amber-400 mb-2">📻 Llamado a Carga</p>
              <h2 className="text-2xl font-bold tracking-tight">{selectedShipment.numero_transporte}</h2>
              <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-neutral-400 text-xs uppercase tracking-widest">Conductor</p>
                  <p className="font-medium">{selectedShipment.conductor_nombre}</p>
                  <p className="text-neutral-400 text-xs font-mono">{selectedShipment.conductor_telefono}</p>
                </div>
                <div>
                  <p className="text-neutral-400 text-xs uppercase tracking-widest">Vehículo</p>
                  <p className="font-mono">{selectedShipment.vehiculo_patente}</p>
                  <p className="text-neutral-400 text-xs">→ {selectedShipment.destino}</p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Andén *</label>
                  <input
                    type="text"
                    value={callForm.anden}
                    onChange={(e) => setCallForm({ ...callForm, anden: e.target.value })}
                    data-testid="call-anden-input"
                    placeholder="A-3"
                    className="w-full px-4 py-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-lg font-mono font-bold uppercase text-center"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Hora *</label>
                  <input
                    type="time"
                    value={callForm.hora}
                    onChange={(e) => setCallForm({ ...callForm, hora: e.target.value })}
                    data-testid="call-hora-input"
                    className="w-full px-4 py-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-lg font-mono font-bold"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Mensaje adicional (opcional)</label>
                <textarea
                  value={callForm.mensaje_adicional}
                  onChange={(e) => setCallForm({ ...callForm, mensaje_adicional: e.target.value })}
                  data-testid="call-mensaje-input"
                  rows={3}
                  placeholder="Ej: Traer casco de seguridad, presentarse en portería primero..."
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm resize-none"
                />
              </div>

              <div className="p-4 bg-neutral-50 border border-neutral-200 rounded-md">
                <p className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Preview del mensaje</p>
                <div className="bg-white p-3 rounded border-l-4 border-green-500 text-xs whitespace-pre-line text-neutral-800 font-mono">
{`🚚 SRoute - Llamado a Carga

Hola ${selectedShipment.conductor_nombre},

Tu transporte ${selectedShipment.numero_transporte} está listo.

📍 Andén: ${callForm.anden || '—'}
🕐 Hora: ${callForm.hora || '—'}
🎯 Destino: ${selectedShipment.destino}${callForm.mensaje_adicional ? '\n\n📝 ' + callForm.mensaje_adicional : ''}`}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-neutral-200">
                <button
                  onClick={() => setShowCallModal(false)}
                  className="px-4 py-2 bg-neutral-100 text-neutral-700 rounded-md hover:bg-neutral-200 text-sm font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCallToLoad}
                  disabled={sending || !callForm.anden || !callForm.hora}
                  data-testid="submit-call-btn"
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-60 text-sm font-medium"
                >
                  <PaperPlaneTilt size={16} className="mr-2" weight="bold" />
                  {sending ? 'Enviando...' : 'Enviar WhatsApp'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Evidence Modal */}
      {showEvidenceModal && evidenceShipment && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="evidence-modal">
          <div className="bg-white rounded-md max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-neutral-200">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-1">Evidencias & Entregas</p>
                <h2 className="text-xl font-bold text-neutral-900">
                  {evidenceShipment.numero_transporte}
                </h2>
                <p className="text-sm text-neutral-600 mt-1">
                  {evidenceShipment.conductor_nombre} · {evidenceShipment.vehiculo_patente}
                </p>
              </div>
              <button
                onClick={() => setShowEvidenceModal(false)}
                data-testid="close-evidence-modal-btn"
                className="p-1 hover:bg-neutral-100 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {loadingEvidences ? (
                <div className="text-center py-12 text-neutral-500">Cargando...</div>
              ) : (
                <>
                  {/* Deliveries Section */}
                  {deliveries.length > 0 && (
                    <div className="mb-6">
                      <div className="flex items-center mb-3">
                        <ListChecks size={18} className="text-neutral-500 mr-2" />
                        <h3 className="text-sm uppercase tracking-widest text-neutral-700 font-semibold">
                          Entregas ({deliveries.length})
                        </h3>
                      </div>
                      <div className="space-y-2">
                        {deliveries.map(d => {
                          const estBadge = {
                            pendiente: 'bg-neutral-100 text-neutral-700 border-neutral-300',
                            total: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                            parcial: 'bg-amber-50 text-amber-700 border-amber-300',
                            no_entregado: 'bg-red-50 text-red-700 border-red-200',
                          }[d.estado] || 'bg-neutral-100 text-neutral-700 border-neutral-300';
                          
                          // Fotos asociadas a esta entrega
                          const deliveryPhotos = evidences.filter(e => e.delivery_id === d.id);
                          
                          return (
                            <div
                              key={d.id}
                              className="border border-neutral-200 rounded-md p-3 hover:bg-neutral-50"
                              data-testid={`delivery-item-${d.id}`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="text-xs font-mono text-blue-700 font-bold">
                                      #{d.orden || '—'}
                                    </span>
                                    <span className="text-xs uppercase tracking-widest text-neutral-500">
                                      {d.numero_entrega}
                                    </span>
                                    <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 rounded ${estBadge}`}>
                                      {d.estado?.replace('_', ' ')}
                                    </span>
                                    {d.guia_validada && (
                                      <span className="inline-flex items-center text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded">
                                        <CheckCircle size={10} weight="fill" className="mr-1" />
                                        Guía OK
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm font-medium text-neutral-900 truncate">
                                    {d.cliente_nombre}
                                  </p>
                                  <p className="text-xs text-neutral-600 truncate">
                                    <MapPinLine size={12} className="inline mr-1" />
                                    {d.cliente_direccion}
                                  </p>
                                  <p className="text-xs text-neutral-500 mt-1 font-mono">
                                    Guía: {d.numero_guia}
                                    {d.guia_ocr_numero_detectado && (
                                      <span className="ml-2 text-neutral-400">
                                        · Detectado: {d.guia_ocr_numero_detectado}
                                      </span>
                                    )}
                                  </p>
                                  {d.observaciones && (
                                    <p className="text-xs text-amber-700 mt-1 italic">
                                      "{d.observaciones}"
                                    </p>
                                  )}
                                </div>
                                {/* Miniaturas de fotos asociadas a la entrega (~30x30mm ≈ 114px) */}
                                {deliveryPhotos.length > 0 && (
                                  <div
                                    className="flex flex-wrap gap-1.5 flex-shrink-0"
                                    data-testid={`delivery-photos-${d.id}`}
                                    style={{ maxWidth: '250px' }}
                                  >
                                    {deliveryPhotos.map(photo => (
                                      <button
                                        key={photo.id}
                                        onClick={() => setLightboxFileId(photo.id)}
                                        data-testid={`delivery-photo-thumb-${photo.id}`}
                                        title={`${photo.tipo} · ${new Date(photo.created_at).toLocaleString('es-CL')}`}
                                        className="relative group border border-neutral-300 rounded overflow-hidden bg-neutral-100 hover:border-blue-600 hover:shadow-md transition-shadow"
                                        style={{ width: '114px', height: '114px' }}
                                      >
                                        <AuthImage
                                          fileId={photo.id}
                                          alt={photo.original_filename}
                                          className="w-full h-full object-cover"
                                          testId={`delivery-thumb-img-${photo.id}`}
                                        />
                                        <span className={`absolute top-1 left-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded font-bold ${
                                          photo.tipo === 'guia' ? 'bg-amber-500 text-white' :
                                          photo.tipo === 'firma' ? 'bg-blue-600 text-white' :
                                          photo.tipo === 'foto_entrega' ? 'bg-emerald-600 text-white' :
                                          'bg-neutral-700 text-white'
                                        }`}>
                                          {photo.tipo}
                                        </span>
                                        <span className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-colors">
                                          <Eye size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" weight="fill" />
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Evidences Section - only evidences NOT linked to a specific delivery */}
                  {(() => {
                    const orphanEvidences = evidences.filter(e => !e.delivery_id);
                    return (
                  <div>
                    <div className="flex items-center mb-3">
                      <Camera size={18} className="text-neutral-500 mr-2" />
                      <h3 className="text-sm uppercase tracking-widest text-neutral-700 font-semibold">
                        Fotos generales del viaje ({orphanEvidences.length})
                      </h3>
                    </div>
                    {orphanEvidences.length === 0 ? (
                      <div className="text-center py-6 text-neutral-400 border border-dashed border-neutral-200 rounded">
                        <Camera size={24} className="mx-auto mb-1 text-neutral-300" />
                        <p className="text-xs">Sin fotos generales</p>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2" data-testid="general-photos">
                        {orphanEvidences.map(e => (
                          <button
                            key={e.id}
                            onClick={() => setLightboxFileId(e.id)}
                            data-testid={`evidence-item-${e.id}`}
                            title={`${e.tipo} · ${new Date(e.created_at).toLocaleString('es-CL')}`}
                            className="relative group border border-neutral-300 rounded overflow-hidden bg-neutral-100 hover:border-blue-600 hover:shadow-md transition-shadow"
                            style={{ width: '114px', height: '114px' }}
                          >
                            <AuthImage
                              fileId={e.id}
                              alt={e.original_filename}
                              className="w-full h-full object-cover"
                              testId={`evidence-img-${e.id}`}
                            />
                            <span className={`absolute top-1 left-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded font-bold ${
                              e.tipo === 'guia' ? 'bg-amber-500 text-white' :
                              e.tipo === 'firma' ? 'bg-blue-600 text-white' :
                              e.tipo === 'foto_entrega' ? 'bg-emerald-600 text-white' :
                              'bg-neutral-700 text-white'
                            }`}>
                              {e.tipo}
                            </span>
                            <span className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-colors">
                              <Eye size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" weight="fill" />
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Lightbox: preview de imagen grande */}
      {lightboxFileId && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4"
          onClick={() => setLightboxFileId(null)}
          data-testid="lightbox-modal"
        >
          <button
            onClick={() => setLightboxFileId(null)}
            data-testid="lightbox-close-btn"
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white"
          >
            <X size={24} weight="bold" />
          </button>
          <div className="max-w-6xl max-h-[92vh] w-full" onClick={(e) => e.stopPropagation()}>
            <AuthImage
              fileId={lightboxFileId}
              alt="Evidencia"
              className="w-full h-full object-contain max-h-[92vh]"
              testId="lightbox-image"
            />
          </div>
        </div>
      )}

      {/* Edit Shipment Modal */}
      {showEditModal && editShipment && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="edit-modal">
          <div className="bg-white rounded-md max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-neutral-200">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-1">Edit</p>
                <h2 className="text-xl font-bold text-neutral-900">Editar Transporte</h2>
                <p className="text-xs text-neutral-500 mt-1 font-mono">{editShipment.numero_transporte}</p>
              </div>
              <button
                onClick={() => { setShowEditModal(false); setEditShipment(null); }}
                data-testid="close-edit-modal-btn"
                className="p-1 hover:bg-neutral-100 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">N° Transporte *</label>
                  <input
                    type="text"
                    value={editForm.numero_transporte}
                    onChange={(e) => setEditForm({ ...editForm, numero_transporte: e.target.value })}
                    data-testid="edit-numero-input"
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Patente Vehículo</label>
                  <input
                    type="text"
                    value={editForm.vehiculo_patente}
                    onChange={(e) => setEditForm({ ...editForm, vehiculo_patente: e.target.value.toUpperCase() })}
                    data-testid="edit-patente-input"
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm font-mono uppercase"
                  />
                </div>
              </div>

              {/* RUT PRIMERO */}
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">RUT Conductor *</label>
                <input
                  type="text"
                  value={editForm.conductor_rut}
                  onChange={handleEditRutChange}
                  onBlur={handleEditRutBlur}
                  data-testid="edit-conductor-rut-input"
                  placeholder="12.345.678-9"
                  className={`w-full px-4 py-2.5 border rounded-md focus:outline-none focus:ring-2 text-sm font-mono ${
                    editRutError 
                      ? 'border-red-300 focus:ring-red-500 bg-red-50' 
                      : 'border-neutral-300 focus:ring-blue-700'
                  }`}
                />
                {editRutError && (
                  <p className="mt-1 text-xs text-red-600">{editRutError}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Conductor *</label>
                  <input
                    type="text"
                    value={editForm.conductor_nombre}
                    onChange={(e) => setEditForm({ ...editForm, conductor_nombre: e.target.value })}
                    data-testid="edit-conductor-nombre-input"
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Teléfono *</label>
                  <input
                    type="text"
                    value={editForm.conductor_telefono}
                    onChange={(e) => setEditForm({ ...editForm, conductor_telefono: e.target.value })}
                    data-testid="edit-conductor-telefono-input"
                    placeholder="+56912345678"
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Origen</label>
                  <input
                    type="text"
                    value={editForm.origen}
                    onChange={(e) => setEditForm({ ...editForm, origen: e.target.value })}
                    data-testid="edit-origen-input"
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Destino</label>
                  <input
                    type="text"
                    value={editForm.destino}
                    onChange={(e) => setEditForm({ ...editForm, destino: e.target.value })}
                    data-testid="edit-destino-input"
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Fecha Carga</label>
                  <input
                    type="date"
                    value={editForm.fecha_carga}
                    onChange={(e) => setEditForm({ ...editForm, fecha_carga: e.target.value })}
                    data-testid="edit-fecha-input"
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Hora Carga</label>
                  <input
                    type="time"
                    value={editForm.hora_carga}
                    onChange={(e) => setEditForm({ ...editForm, hora_carga: e.target.value })}
                    data-testid="edit-hora-input"
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Andén</label>
                  <input
                    type="text"
                    value={editForm.anden}
                    onChange={(e) => setEditForm({ ...editForm, anden: e.target.value.toUpperCase() })}
                    data-testid="edit-anden-input"
                    placeholder="A-3"
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm font-mono uppercase text-center font-bold"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Observaciones</label>
                <textarea
                  value={editForm.observaciones}
                  onChange={(e) => setEditForm({ ...editForm, observaciones: e.target.value })}
                  data-testid="edit-observaciones-input"
                  rows={3}
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 p-6 border-t border-neutral-200 bg-neutral-50">
              <button
                onClick={() => { setShowEditModal(false); setEditShipment(null); }}
                data-testid="cancel-edit-btn"
                className="px-4 py-2 bg-white border border-neutral-300 text-neutral-700 rounded-md hover:bg-neutral-100 text-sm font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={savingEdit}
                data-testid="save-edit-btn"
                className="flex items-center px-4 py-2 bg-blue-700 text-white rounded-md hover:bg-blue-800 disabled:opacity-60 text-sm font-medium"
              >
                <CheckCircle size={16} className="mr-2" weight="bold" />
                {savingEdit ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Bulk action bar - floating at bottom when items selected */}
      {selectedIds.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-neutral-900 text-white rounded-lg shadow-2xl flex items-center gap-4 pl-5 pr-3 py-3 border border-neutral-800"
          data-testid="bulk-actions-bar"
        >
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 bg-blue-600 text-white text-xs font-bold rounded font-mono">
              {selectedIds.size}
            </span>
            <span className="text-xs uppercase tracking-widest font-medium">
              seleccionado{selectedIds.size === 1 ? '' : 's'}
            </span>
          </div>
          <div className="h-6 w-px bg-neutral-700"></div>
          <button
            onClick={clearSelection}
            data-testid="bulk-clear-btn"
            className="text-xs uppercase tracking-widest text-neutral-400 hover:text-white transition-colors font-medium"
          >
            Deseleccionar
          </button>
          <button
            onClick={handleBulkDelete}
            disabled={bulkDeleting}
            data-testid="bulk-delete-btn"
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 rounded text-xs uppercase tracking-widest font-bold transition-colors"
          >
            <Trash size={14} weight="bold" />
            {bulkDeleting ? 'Borrando...' : 'Borrar seleccionados'}
          </button>
        </div>
      )}
    </div>
  );
}
